import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router';
import I18n from 'i18n-js';
import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import { toggleAppbarAction } from 'actions/app-actions';
import { ROUTES_V2_PATHS } from 'constants/route-constants';
import { selectedCitySelector } from 'selectors/app-selector';
import ConfirmModal from 'components/v2/confirm-modal';
import useUnloadConfirm from 'components/v2/anytime-booking/common/use-unload-confirm';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';

import StepPage from './common/step-page';
import UploadFile from './upload-file';
import CheckUpload from './check-upload';
import SetUpDeliveries from './set-up-deliveries';
import PayAndBook from './pay-and-book';
import BulkUploadPageContext from './common/context';

const StepContentComponents = [
  undefined,
  UploadFile,
  CheckUpload,
  SetUpDeliveries,
  PayAndBook,
];

const BulkUploadPage = ({ history, city }) => {
  const dispatch = useDispatch();

  useEffect(
    () => {
      dispatch(toggleAppbarAction());
      return () => {
        dispatch(toggleAppbarAction());
      };
    },
    [dispatch],
  );

  useEffect(
    () => {
      // Trigger load metadata only after city is loaded
      if (city) {
        dispatch(NewBulkUploadDuck.creators.fetchMetaData());
      }
    },
    [city],
  );

  const totalSteps = 4;
  const [currentStep, setCurrentStep] = useState(1);
  const [backModalVisible, setBackModalVisible] = useState(false);
  const backToHome = useCallback(
    () => {
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressNewBulkUploadStep1Back(),
      );
      history.push(ROUTES_V2_PATHS.home);
    },
    [dispatch, history],
  );

  const stepForwardFunc = useCallback(
    () => {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    },
    [currentStep, setCurrentStep],
  );

  const [pendingGoToStep, setPendingGoToStep] = useState(undefined);
  const [goToStepModalVisible, setGoToStepModalVisible] = useState(false);
  const goToStep = useCallback(
    step => {
      setGoToStepModalVisible(true);
      setPendingGoToStep(step);
    },
    [setCurrentStep, setPendingGoToStep],
  );

  const cancelGoToStep = useCallback(
    () => {
      setGoToStepModalVisible(false);
    },
    [setPendingGoToStep],
  );
  const confirmGoToStep = useCallback(
    () => {
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeBulkUploadNavigateStep(
          currentStep,
          pendingGoToStep,
        ),
      );
      setCurrentStep(pendingGoToStep);
      cancelGoToStep();
    },
    [dispatch, currentStep, setCurrentStep, pendingGoToStep, cancelGoToStep],
  );

  const getStepPhraseKey = step => {
    switch (step) {
      case 1:
        return 'phrase.geWebBulkUpload.express_web_bulk_upload_heading_upload_file_happypath';
      case 2:
        return 'phrase.geWebBulkUpload.express_web_bulk_upload_heading_check_details_happypath';
      case 3:
        return 'phrase.geWebBulkUpload.express_web_bulk_upload_heading_setup_deliveries_happypath';
      case 4:
        return 'phrase.geWebBulkUpload.express_web_bulk_upload_heading_pay_book_happypath';
      default:
        return 'v2.sider.home';
    }
  };

  const { stepBackFunc, stepBackTitle } = useMemo(
    () => {
      if (currentStep > 1) {
        const lastStep = currentStep - 1;
        const lastStepPhraseKey = getStepPhraseKey(lastStep);
        return {
          stepBackFunc: () => goToStep(currentStep - 1),
          stepBackTitle: `< ${I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_button_back_to_happypath',
          )} ${I18n.t(`${lastStepPhraseKey}`)}`,
        };
      }
      return {
        stepBackFunc: () => {
          setBackModalVisible(true);
        },
        stepBackTitle: `< ${I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_button_back_to_happypath',
        )} ${I18n.t('v2.sider.home')}`,
      };
    },
    [currentStep, goToStep, setBackModalVisible],
  );

  useUnloadConfirm(currentStep === 1);

  const StepContentComponent = StepContentComponents[currentStep];
  if (!StepContentComponent) {
    return null;
  }

  return (
    <div>
      <BulkUploadPageContext.Provider
        value={{
          stepBackFunc,
          stepForwardFunc,
          goToStep,
        }}
      >
        <ConfirmModal
          visible={goToStepModalVisible}
          title={`${I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_button_back_to_happypath',
          )} ${pendingGoToStep}: ${I18n.t(
            `${getStepPhraseKey(pendingGoToStep)}`,
          )}?`}
          body={I18n.t('bulkUploadRevamp.navigationConfirm.body')}
          okText={I18n.t('bulkUploadRevamp.navigationConfirm.confirm')}
          cancelText={I18n.t('bulkUploadRevamp.navigationConfirm.cancel')}
          onOk={confirmGoToStep}
          onCancel={cancelGoToStep}
        />
        <ConfirmModal
          visible={backModalVisible}
          title={I18n.t('v2.homepage.goBackConfirmModal.heading')}
          body={I18n.t('v2.homepage.goBackConfirmModal.body')}
          okText={I18n.t('v2.homepage.goBackConfirmModal.yes')}
          cancelText={I18n.t('v2.homepage.goBackConfirmModal.no')}
          onOk={backToHome}
          onCancel={() => setBackModalVisible(false)}
        />
        <StepPage
          goToStep={goToStep}
          stepBackFunc={stepBackFunc}
          stepBackTitle={stepBackTitle}
          currentStep={currentStep}
          stepName={I18n.t(`${getStepPhraseKey(currentStep)}`)}
          totalSteps={totalSteps}
        >
          <StepContentComponent />
        </StepPage>
      </BulkUploadPageContext.Provider>
    </div>
  );
};

BulkUploadPage.propTypes = {
  history: PropTypes.shape({}).isRequired,
  city: PropTypes.shape({}),
};

BulkUploadPage.defaultProps = {
  city: null,
};

export default withRouter(
  connect(state => ({
    city: selectedCitySelector(state),
  }))(BulkUploadPage),
);
